// Kudos https://github.com/goenning/next-applicationinsights/blob/master/src/index.ts
import { type ReactNode, useEffect } from 'react';

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useRouter } from 'next/router';
import { propOr } from 'ramda';

import type { User } from '@src/types/members';
import type { Room } from '@src/types/rooms';

import { track } from '@framework/mixpanel/mixpanel.utils';
import { isWindowDefined } from '@framework/utils';
import { decamelizeKeys } from '@framework/utils/camelize.utils';
import {
    getInsightPayload,
    appInsightsSessionKey,
    getURLWithMaskedRoomName
} from '@framework/utils/insights';

import useActiveRoom from '@hooks/rooms/useActiveRoom';
import useAuthenticationStatus from '@hooks/useAuthenticationStatus';
import useLoggedUser from '@hooks/useLoggedUser';
import useSendInsight from '@hooks/useSendInsight';

import { useRuntimeContext } from '@components/pages/other/RuntimeContext/useRuntimeContext';

import { PAGE_LEFT, PAGE_VISIBILITY_CHANGE } from './constants/insights';

import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

const getId = propOr('', 'id');

let appInsights: ApplicationInsights;

type ApplicationInsightsProviderProps = {
    config: {
        isEnabled: boolean;
        trackPageView: boolean;
    };
    appView: string;
    children: ReactNode;
};

/**
 * Helper function used to debug which insights are being tracked
 * it can be enabled by setting APP_INSIGHTS_DEBUG_MODE_ENABLED in localStorage to true
 * @param type Type of the insight either proper insight or page-view
 * @param data content of the insight
 */
const debugOutputInsight = (type: 'insight' | 'page-view', data: any) => {
    if (JSON.parse(localStorage.getItem('APP_INSIGHTS_DEBUG_MODE_ENABLED') ?? 'false')) {
        /* eslint-disable no-console */
        console.log('AppInsightDebug', {
            type,
            ...data
        });
    }
};

/**
 * A telemetry initializer that anonymizes response text in telemetry data to prevent sensitive data from being logged.
 * @param {ITelemetryItem} envelope - The telemetry envelope containing data to be sent to Application Insights
 */
const anonymizationTelemetryInitializer = (envelope: ITelemetryItem) => {
    if (envelope?.baseData?.properties?.responseText) {
        // eslint-disable-next-line no-param-reassign
        envelope.baseData.properties.responseText = '';
    }
};

const ApplicationInsightsProvider = ({
    config,
    appView,
    children
}: ApplicationInsightsProviderProps) => {
    const { data: proofStatus } = useAuthenticationStatus();
    const isAuthenticated = Boolean(proofStatus?.isAuthenticated);
    const { data: loggedUser } = useLoggedUser({
        enabled: isAuthenticated
    });
    const activeRoom = useActiveRoom(isAuthenticated);
    const router = useRouter();

    const sendInsight = useSendInsight();

    const { MEMBERS_APPLICATION_INSIGHTS_API_KEY, SECRETARIES_APPLICATION_INSIGHTS_API_KEY } =
        useRuntimeContext();

    // Initialize the insights
    const initializeAppInsights = () => {
        if (isWindowDefined() && config.isEnabled && !appInsights) {
            const instrumentationKey =
                appView === 'members'
                    ? MEMBERS_APPLICATION_INSIGHTS_API_KEY
                    : SECRETARIES_APPLICATION_INSIGHTS_API_KEY;
            appInsights = new ApplicationInsights({
                config: { ...config, instrumentationKey }
            });
            appInsights.loadAppInsights();
            appInsights.addTelemetryInitializer(anonymizationTelemetryInitializer);
            window.appInsights = appInsights;
        }
    };

    useEffect(() => {
        initializeAppInsights();
    }, [appView]);

    // Track the page view each time a Path changes
    const trackPageView = () => {
        const pageViewData = {
            name: router.pathname,
            properties: decamelizeKeys({
                stack: 'react',
                appSessionId: appInsightsSessionKey,
                roomId: getId(activeRoom ?? {}),
                userId: getId(loggedUser ?? {})
            })
        };
        if (appInsights && config.isEnabled && config.trackPageView) {
            appInsights.trackPageView(pageViewData);
        }
        debugOutputInsight('page-view', pageViewData);
    };
    useEffect(() => {
        trackPageView();
    }, [router.asPath]);

    // Register hook for page left event to send a final insight before the tab/page is closed.
    useEffect(() => {
        window.addEventListener('beforeunload', () => {
            sendInsight(PAGE_LEFT);
        });
    }, []);

    // Register hook for visibility change event to detect if user switched between tabs
    useEffect(() => {
        window.addEventListener('blur', () => {
            sendInsight(PAGE_VISIBILITY_CHANGE, {
                visible: false
            });
        });
    }, []);

    // Register hook for visibility change event to detect if user switched between tabs
    useEffect(() => {
        window.addEventListener('focus', () => {
            sendInsight(PAGE_VISIBILITY_CHANGE, {
                visible: true
            });
        });
    }, []);

    return children;
};

export type State = {
    currentAppSection: string;
    isAttendeeView: boolean;
    activeRoom: Room | undefined;
    loggedMember: User | undefined;
    currentPermission?: string;
};

type TrackInsightParams = {
    data?: { [key: string]: unknown };
    insightsEnabled?: boolean;
    mixpanelEnabled?: boolean;
    name: string;
    state?: State;
};

/**
 * Wrapper function to track insights, it decamelizes all the keys and adds additional properties
 * @param name Name of the insight
 * @param data Payload of the insight ( will be decamelized )
 * @param state (optional) Current redux state, will be used to enhance insight with info about user, room, etc...
 */
// eslint-disable-next-line default-param-last
export const trackInsight = ({
    data,
    insightsEnabled,
    name,
    state,
    mixpanelEnabled
}: TrackInsightParams) => {
    if (isWindowDefined()) {
        const insightsPayload = getInsightPayload(state, data);
        const insightContent = {
            name,
            properties: {
                ...insightsPayload,
                url: window.location.href
            }
        };
        if (mixpanelEnabled) {
            track(name, {
                ...insightsPayload,
                url: getURLWithMaskedRoomName()
            });
        }
        if (window.appInsights && insightsEnabled) {
            window.appInsights.trackEvent(insightContent);
        }
        debugOutputInsight('insight', insightContent);
    }
};

export default ApplicationInsightsProvider;
